<template>
  <v-container fluid>
    <v-row>
      <!-- Analysis Results -->
      <v-col v-if="loadingRelated" cols="12">
        <v-progress-linear indeterminate/>
      </v-col>
      <v-col v-if="!loadingRelated" cols="12">
        <v-card>
          <v-card-title>
            <strong>{{ cleanTaskTitle(title) || 'Untitled Task' }}</strong>
            <v-spacer/>
            <div v-if="apiRate" class="mr-4 d-flex align-center">
              <span class="text-h5 font-weight-bold primary--text">
                {{ apiRate }} kg/hr
              </span>
            </div>
            <v-alert v-if="!apiRate" outlined type="warning">
              No Rate Set
            </v-alert>
            <v-chip
              v-if="currentEntryType"
              class="mr-2"
              color="info"
              small
              text-color="white"
            >
              {{ currentEntryType }}
            </v-chip>
            <v-chip
              v-if="currentUserDisplay"
              class="mr-2"
              color="success"
              small
              text-color="white"
            >
              {{ currentUserDisplay.first_name }} ({{ currentUserDisplay.initials }})
            </v-chip>
            <v-chip
              v-if="currentTimestamp"
              class="mr-2"
              color="grey"
              small
              text-color="white"
            >
              {{ formatTimestamp(currentTimestamp) }}
            </v-chip>
            <v-spacer/>

            <v-btn
              class="ml-2"
              icon
              small
              title="Back to task list"
              @click="goBackToTaskList"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <!-- Charts Section -->
            <v-row v-if="relatedTasks.length > 0" class="mb-4">
              <v-col cols="12" md="3">
                <v-card flat>

                  <v-card-text>
                    <p>Adjust the rate</p>

                    <v-form ref="inlineRateForm" v-model="inlineRateFormValid">
                      <v-row dense>
                        <v-col cols="6">
                          <div class="text-caption mb-1">Rate (kg/hr)</div>
                          <v-text-field
                            ref="inlineRateInput"
                            v-model.number="inlineRate"
                            :rules="rateRules"
                            density="compact"
                            hide-details="auto"
                            min="0"
                            outlined
                            step="0.1"
                            type="number"
                          />
                        </v-col>
                        <v-col cols="6">
                          <div class="text-caption mb-1">Fixed Time (hrs)</div>
                          <v-text-field
                            v-model.number="inlineFixedTime"
                            :rules="fixedTimeRules"
                            density="compact"
                            disabled
                            hide-details="auto"
                            min="0"
                            outlined
                            step="0.1"
                            type="number"
                          />
                        </v-col>
                      </v-row>
                      <v-row class="mt-2" dense>
                        <v-col cols="6">
                          <v-btn
                            v-if="apiMedianRateData && apiMedianRateData.medianRate > 0"
                            block
                            color="success"
                            outlined
                            small
                            @click="inlineRate = apiMedianRateData.medianRate"
                          >
                            Median ({{ formatRate(apiMedianRateData.medianRate) }})
                          </v-btn>
                        </v-col>
                        <v-col cols="6">
                          <v-btn
                            v-if="currentTaskRate && currentTaskRate > 0"
                            block
                            color="info"
                            outlined
                            small
                            @click="inlineRate = averageRate"
                          >
                            Avg ({{ formatRate(averageRate) }})
                          </v-btn>
                        </v-col>
                      </v-row>
                      <v-row class="mt-2" dense>
                        <v-col cols="12">
                          <v-btn
                            :disabled="!inlineRateFormValid || inlineRateSaving || !hasRateChanged"
                            :loading="inlineRateSaving"
                            block
                            color="primary"
                            small
                            @click="saveInlineProcessingRate"
                          >
                            Save Rate
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-form>

                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined>
                  <!--                  <v-card-title class="text-h6">Duration vs Weight</v-card-title>-->
                  <v-card-text>
                    <div style="height: 300px;">
                      <DurationVsWeightChart :tasks="chartTasks"/>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined>
                  <!--                  <v-card-title class="text-h6">Rate Distribution</v-card-title>-->
                  <v-card-text>
                    <div style="height: 300px;">
                      <RateDistributionChart :tasks="chartTasks"/>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined>
                  <!--                  <v-card-title class="text-h6">Weight Distribution</v-card-title>-->
                  <v-card-text>
                    <div style="height: 300px;">
                      <WeightDistributionChart :tasks="chartTasks"/>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Duration Examples -->
            <v-row v-if="relatedTasks.length > 0" class="mb-4">
              <v-col cols="12">
                <v-card outlined>
                  <v-card-title class="text-h6">Duration Examples (Top 4 Weight Ranges)</v-card-title>
                  <v-card-text>
                    <div v-if="inlineRateExamples.length > 0">
                      <v-row>
                        <v-col
                          v-for="(example, index) in inlineRateExamples"
                          :key="index"
                          cols="12"
                          md="3"
                        >
                          <v-card class="pa-3 text-center" outlined>
                            <div class="text-h6 primary--text">{{ example.weightFormatted }}</div>
                            <div class="text-caption mb-2">Range: {{ example.binRange }}</div>
                            <div class="text-h5 font-weight-bold">{{ example.durationFormatted }}</div>
                            <div class="text-caption">{{ example.taskCount }} tasks in this range</div>
                          </v-card>
                        </v-col>
                      </v-row>
                    </div>
                    <div v-else class="text-center text-caption grey--text">
                      Enter a processing rate above to see duration examples
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Outlier Detection Stats -->
            <v-card v-if="relatedTasks.length > 0" class="mb-4" outlined>
              <v-card-text>
                <v-row>
                  <v-col cols="3">
                    <v-alert class="mt-9" outlined type="info">
                      Samples: {{ filteredRelatedTasks.length }} (of {{ relatedTasksData.length }})<br/>
                      Rate (trend): {{ trendRate }} kg/hr<br/>
                      Rate (median): {{ medianRate }} kg/hr<br/>
                      Rate (avg): {{ averageRate }} kg/hr<br/>
                      <span v-if="apiMedianRateData">Rate (API): {{ apiMedianRateData.medianRate }} kg/hr</span>
                    </v-alert>

                  </v-col>
                  <v-col>
                    <v-toolbar flat>
                      <strong>Outlier Detection Statistics</strong>
                      <v-spacer/>
                      <v-btn-toggle v-model="outlierFilterMode" dense>
                        <v-btn small value="strict">Q1/Q3 (Stricter)</v-btn>
                        <v-btn small value="standard">IQR (Standard)</v-btn>
                        <v-btn small value="none">No Filter</v-btn>
                      </v-btn-toggle>
                    </v-toolbar>

                    <div v-if="relatedTasks.length < 4">
                      <v-alert dense type="info">
                        Not enough samples for outlier detection ({{ relatedTasks.length }} tasks, need at least 4)
                      </v-alert>
                    </div>
                    <div v-else>
                      <!-- Rate Outlier Stats -->
                      <v-row class="" dense>
                        <v-col cols="12" md="2">
                          <strong>Rate Outliers (kg/hr)</strong>
                        </v-col>
                        <v-col cols="12" md="2">
                          <strong>Q1:</strong> {{ rateOutlierStats.q1.toFixed(2) }} kg/hr
                        </v-col>
                        <v-col cols="12" md="2">
                          <strong>Q3:</strong> {{ rateOutlierStats.q3.toFixed(2) }} kg/hr
                        </v-col>
                        <v-col cols="12" md="2">
                          <strong>IQR:</strong> {{ rateOutlierStats.iqr.toFixed(2) }} kg/hr
                        </v-col>
                        <v-col cols="12" md="2">
                          <strong>Lower Bound:</strong>
                          {{ outlierFilterMode === 'none' ? 'None' : currentRateBounds.lower.toFixed(2) + ' kg/hr' }}
                        </v-col>
                        <v-col cols="12" md="2">
                          <strong>Upper Bound:</strong>
                          {{ outlierFilterMode === 'none' ? 'None' : currentRateBounds.upper.toFixed(2) + ' kg/hr' }}
                        </v-col>
                      </v-row>


                      <!-- Duration Outlier Stats -->
                      <v-row dense>
                        <v-col cols="12" md="2">
                          <strong>Duration Outliers (hours)</strong>
                        </v-col>
                        <v-col cols="12" md="2">
                          <strong>Q1:</strong> {{ durationOutlierStats.q1.toFixed(2) }} hrs
                        </v-col>
                        <v-col cols="12" md="2">
                          <strong>Q3:</strong> {{ durationOutlierStats.q3.toFixed(2) }} hrs
                        </v-col>
                        <v-col cols="12" md="2">
                          <strong>IQR:</strong> {{ durationOutlierStats.iqr.toFixed(2) }} hrs
                        </v-col>
                        <v-col cols="12" md="2">
                          <strong>Lower Bound:</strong>
                          {{ outlierFilterMode === 'none' ? 'None' : currentDurationBounds.lower.toFixed(2) + ' hrs' }}
                        </v-col>
                        <v-col cols="12" md="2">
                          <strong>Upper Bound:</strong>
                          {{ outlierFilterMode === 'none' ? 'None' : currentDurationBounds.upper.toFixed(2) + ' hrs' }}
                        </v-col>
                      </v-row>
                    </div>

                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-toolbar class="mb-2" dense flat>
              <strong>Filter by Team Members:</strong>
              <v-spacer/>
              <v-btn v-if="memberOptions.length" small text @click="selectAllMembers">Select All</v-btn>
            </v-toolbar>
            <div v-if="memberOptions.length" class="mb-4 text-center">
              <v-btn-toggle v-model="selectedMembers" dense multiple>
                <v-btn
                  v-for="name in memberOptions"
                  :key="name"
                  :value="name"
                  small
                >{{ name }}
                </v-btn>
              </v-btn-toggle>
            </div>

            <!-- Search and Date Toolbar -->
<!--            <v-toolbar class="mb-2" flat>-->
<!--              <v-text-field-->
<!--                v-model="search"-->
<!--                append-icon="mdi-magnify"-->
<!--                class="d-print-none"-->
<!--                clearable-->
<!--                hide-details-->
<!--                label="Search Tasks"-->
<!--                single-line-->
<!--              />-->
<!--              <v-spacer/>-->
<!--              <v-text-field :value="datesFormatted"-->
<!--                            append-icon="mdi-calendar"-->
<!--                            hint="Task Dates"-->
<!--                            label="Select dates"-->
<!--                            persistent-hint-->
<!--                            readonly-->
<!--                            single-line-->
<!--                            @click="showDatePicker=true"-->
<!--              />-->

<!--              <v-dialog-->
<!--                ref="dialog"-->
<!--                v-model="showDatePicker"-->
<!--                persistent-->
<!--                width="290px"-->
<!--              >-->
<!--                <v-date-picker-->
<!--                  v-model="dates"-->
<!--                  no-title-->
<!--                  range-->
<!--                ></v-date-picker>-->
<!--                <v-btn @click="showDatePicker=false; (dates||[]).sort()">Close</v-btn>-->
<!--              </v-dialog>-->
<!--            </v-toolbar>-->

            <ChoppingTaskTable
              :headers="relatedTaskHeaders"
              :loading="loadingRelated"
              :outlier-bounds="outlierBounds"
              :outlier-filter-mode="outlierFilterMode"
              :tasks="relatedTasks"
              :teams="teams"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import api from '@/api';
import moment from 'moment';
import urlState from "../router/urlState";
import DurationVsWeightChart from '@/components/charts/DurationVsWeightChart.vue';
import RateDistributionChart from '@/components/charts/RateDistributionChart.vue';
import WeightDistributionChart from '@/components/charts/WeightDistributionChart.vue';
import ChoppingTaskTable from '@/components/ChoppingTaskTable.vue';
import {
  calculateEstimatedDuration,
  calculateRate,
  calculateTotalWeight,
  formatDuration,
  formatRate,
  formatWeight,
  getBinRepresentativeWeight,
  getTopWeightBins
} from '@/utils/tasks/chopping-utils.js';

export default {
  name: "TaskAnalysis",
  mixins: [urlState],
  components: {
    DurationVsWeightChart,
    RateDistributionChart,
    WeightDistributionChart,
    ChoppingTaskTable
  },
  data() {
    return {
      apiRate: null,
      title: null,
      user_display: null,
      timestamp: null,
      loading: false,
      showDatePicker: null,
      dates: [],
      search: null,
      teamFilter: [],
      rawTasks: [],
      teams: {},
      selectedFoodId: null,
      relatedTasksData: [],
      loadingRelated: false,
      outlierFilterMode: 'strict', // Default to Q1/Q3 bounds (stricter)
      apiMedianRateData: null, // Data from the median rate API endpoint
      taskHeaders: [
        {text: 'Task', value: 'title'},
        {text: 'Date', value: 'date'},
        {text: 'Task Times', value: 'executionTimes'},
        {text: 'Total Weight', value: 'totalWeight'},
        {text: 'Duration', value: 'duration'},
        {text: 'Actual Rate (kg/hr)', value: 'rate'},
        {text: 'Estimated Duration', value: 'estimatedDuration'},
        {text: 'Estimated Rate (kg/hr)', value: 'apiRate'},
        {text: 'Performance', value: 'performance'},
        {text: 'Description', value: 'description'},
        // {text: 'Actions', value: 'actions', sortable: false}
      ],
      relatedTaskHeaders: [
        // {text: 'Task', value: 'title'},
        // {text: 'Team', value: 'team'},
        // {text: 'Date', value: 'date'},
        {text: 'Task Times', value: 'executionTimes'},
        {text: 'Members', value: 'memberCount'},
        {text: 'Member Names', value: 'memberNames'},
        {text: 'Duration', value: 'duration'},
        {text: 'Estimated Duration', value: 'estimatedDuration'},
        {text: 'Total Weight', value: 'totalWeight'},
        {text: 'Rate (kg/hr)', value: 'rate'},
        {text: 'Estimated Rate (kg/hr)', value: 'apiRate'},
        {text: 'Performance', value: 'performance'},
        // { text: 'Relation', value: 'relationReason' },
        {text: 'Description', value: 'description'}
      ],
      selectedMembers: [],

      // Rate update dialog
      showRateUpdateDialog: false,
      rateSaving: false,
      rateFormValid: false,
      newRate: null,
      newFixedTime: 0,
      currentEntryType: null, // Store the entry_type for display
      rateRules: [
        v => !!v || 'Processing rate is required',
        v => (v && v > 0) || 'Rate must be greater than 0',
        v => (v && !isNaN(parseFloat(v))) || 'Rate must be a valid number'
      ],
      fixedTimeRules: [
        v => v >= 0 || 'Fixed time must be 0 or greater',
        v => !isNaN(parseFloat(v)) || 'Fixed time must be a valid number'
      ],

      // Inline rate editing
      inlineRateFormValid: false,
      inlineRateSaving: false,
      inlineRate: null,
      inlineFixedTime: 0
    }
  },
  async mounted() {
    this.syncToUrl({
      param: 'dates', urlParam: 'dates',
      initFromRoute: true,
      parseCallback: (v) => Array.isArray(v) ? v : [v]
    });
    // Get selectedFoodId from route params
    await this.$nextTick();
    if (this.dates && this.dates.sort)
      this.dates.sort();
    if (this.foodid) {
      await this.selectFoodId(this.foodid);
    }

    console.log('mounted', this.foodid, this.team)
  },
  watch: {
    relatedTasksData() {
      // default select all members when related tasks load/update
      const all = this.memberOptions;
      this.selectedMembers = this.selectedMembers.filter(s => all.includes(s));
    }
  }
  ,
  computed: {
    memberOptions() {
      // Unique member names from relatedTasksData
      const set = new Set();
      for (const task of this.relatedTasksData) {
        for (const name of this.getTaskMemberNames(task)) {
          set.add(name);
        }
      }
      return Array.from(set).sort();
    },
    relatedTasks() {
      const selected = this.selectedMembers;
      if (!selected || selected.length === 0) return this.relatedTasksData;
      // Only include tasks that include all selected members
      return this.relatedTasksData.filter(task => {
        const names = this.getTaskMemberNames(task);
        return selected.some(s => names.includes(s));
      });
    },
    datesFormatted() {
      const format = 'dddd MMMM D';
      const from = this.dates[0];
      const to = this.dates[1];
      if (!from) {
        return '';
      } else {
        if (from === to) {
          return `${moment(from).format(format)}`;
        } else {
          return `${moment(from).format(format)} - ${moment(to).format(format)}`
        }
      }
    },
    chartTasks() {
      const tasks = [];
      // Only include non-outlier related tasks
      tasks.push(...this.filteredRelatedTasks);
      return tasks;
    },
    filteredRelatedTasks() {
      return this.relatedTasks.filter(task => !this.isRateOutlier(task) && !this.isDurationOutlier(task));
    },
    estimatedDuration() {
      if (this.filteredRelatedTasks.length < 2) {
        return null;
      }

      const selectedWeight = calculateTotalWeight(this.description) / 1000; // Convert to kg

      // Calculate linear regression from filtered related tasks only (excluding outliers)
      const relatedData = this.filteredRelatedTasks.map(task => ({
        x: calculateTotalWeight(task.description) / 1000, // weight in kg
        y: task.elapsed / (1000 * 60 * 60) // duration in hours
      }));

      if (relatedData.length < 2) return null;

      const n = relatedData.length;
      const sumX = relatedData.reduce((sum, point) => sum + point.x, 0);
      const sumY = relatedData.reduce((sum, point) => sum + point.y, 0);
      const sumXY = relatedData.reduce((sum, point) => sum + (point.x * point.y), 0);
      const sumXX = relatedData.reduce((sum, point) => sum + (point.x * point.x), 0);

      const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
      const intercept = (sumY - slope * sumX) / n;

      // Estimate duration for selected task weight
      return slope * selectedWeight + intercept;
    },

    rateOutlierStats() {
      if (this.relatedTasksData.length < 4) {
        return {q1: 0, q3: 0, iqr: 0, lowerBound: 0, upperBound: 0};
      }

      const rates = this.relatedTasksData.map(t => calculateRate(t.description, t.elapsed));
      const validRates = rates.filter(rate => rate > 0);

      if (validRates.length < 4) {
        return {q1: 0, q3: 0, iqr: 0, lowerBound: 0, upperBound: 0};
      }

      const sortedRates = [...validRates].sort((a, b) => a - b);
      const q1 = sortedRates[Math.floor(sortedRates.length * 0.25)];
      const q3 = sortedRates[Math.floor(sortedRates.length * 0.75)];
      const iqr = q3 - q1;
      const lowerBound = q1 - 1.5 * iqr;
      const upperBound = q3 + 1.5 * iqr;

      return {q1, q3, iqr, lowerBound, upperBound};
    },
    durationOutlierStats() {
      if (this.relatedTasksData.length < 4) {
        return {q1: 0, q3: 0, iqr: 0, lowerBound: 0, upperBound: 0};
      }

      const durations = this.relatedTasksData.map(t => t.elapsed / (1000 * 60 * 60)); // Convert to hours
      const validDurations = durations.filter(duration => duration > 0);

      if (validDurations.length < 4) {
        return {q1: 0, q3: 0, iqr: 0, lowerBound: 0, upperBound: 0};
      }

      const sortedDurations = [...validDurations].sort((a, b) => a - b);
      const q1 = sortedDurations[Math.floor(sortedDurations.length * 0.25)];
      const q3 = sortedDurations[Math.floor(sortedDurations.length * 0.75)];
      const iqr = q3 - q1;
      const lowerBound = q1 - 1.5 * iqr;
      const upperBound = q3 + 1.5 * iqr;

      return {q1, q3, iqr, lowerBound, upperBound};
    },
    currentRateBounds() {
      const stats = this.rateOutlierStats;
      if (this.outlierFilterMode === 'strict') {
        return {lower: stats.q1, upper: stats.q3};
      } else if (this.outlierFilterMode === 'standard') {
        return {lower: stats.lowerBound, upper: stats.upperBound};
      } else {
        // No filter mode - use extreme bounds that won't filter anything
        return {lower: -Infinity, upper: Infinity};
      }
    },
    currentDurationBounds() {
      const stats = this.durationOutlierStats;
      if (this.outlierFilterMode === 'strict') {
        return {lower: stats.q1, upper: stats.q3};
      } else if (this.outlierFilterMode === 'standard') {
        return {lower: stats.lowerBound, upper: stats.upperBound};
      } else {
        // No filter mode - use extreme bounds that won't filter anything
        return {lower: -Infinity, upper: Infinity};
      }
    },
    count() {
      return this.filteredRelatedTasks.length;
    },
    medianRate() {
      if (this.filteredRelatedTasks.length === 0) return '0.0';

      const rates = this.filteredRelatedTasks.map(t => calculateRate(t.description, t.elapsed));
      const validRates = rates.filter(rate => rate > 0);

      if (validRates.length === 0) return '0.0';

      const sortedRates = [...validRates].sort((a, b) => a - b);
      const median = sortedRates.length % 2 === 0
        ? (sortedRates[sortedRates.length / 2 - 1] + sortedRates[sortedRates.length / 2]) / 2
        : sortedRates[Math.floor(sortedRates.length / 2)];

      return median.toFixed(1);
    },
    averageRate() {
      if (this.filteredRelatedTasks.length === 0) return '0.0';

      const rates = this.filteredRelatedTasks.map(t => calculateRate(t.description, t.elapsed));
      const validRates = rates.filter(rate => rate > 0);

      if (validRates.length === 0) return '0.0';

      const average = validRates.reduce((sum, rate) => sum + rate, 0) / validRates.length;
      return average.toFixed(1);
    },

    trendRate() {
      if (this.estimatedDuration === null) return '0.0';

      const selectedWeightKg = calculateTotalWeight(this.description) / 1000;

      if (selectedWeightKg === 0 || this.estimatedDuration === 0) return '0.0';

      const impliedRate = selectedWeightKg / this.estimatedDuration;
      return impliedRate.toFixed(1);
    },

    outlierBounds() {
      return {
        rateBounds: this.currentRateBounds,
        durationBounds: this.currentDurationBounds
      };
    },

    currentTaskRate() {
      return calculateRate(this.description, this.elapsed);
    },

    inlineRateExamples() {
      if (!this.relatedTasksData || this.relatedTasksData.length === 0) {
        return [];
      }

      const topBins = getTopWeightBins(this.relatedTasksData, 4);
      const rate = this.inlineRate && this.inlineRate > 0 ? parseFloat(this.inlineRate) : null;

      return topBins.map(bin => {
        const weight = getBinRepresentativeWeight(bin);
        const estimatedDuration = rate ? calculateEstimatedDuration(weight, rate) : null;
        return {
          weight: weight,
          weightFormatted: formatWeight(weight * 1000), // Convert back to grams for formatting
          estimatedDuration: estimatedDuration,
          durationFormatted: estimatedDuration ? formatDuration(estimatedDuration * 1000 * 60 * 60) : '?',
          binRange: `${bin.min.toFixed(1)}-${bin.max.toFixed(1)}kg`,
          taskCount: bin.count
        };
      }).sort((a, b) => a.weight - b.weight); // Sort by increasing weight
    },

    hasRateChanged() {
      if (!this.apiRate) {
        return this.inlineRate && parseFloat(this.inlineRate) > 0;
      }

      const currentApiRate = parseFloat(this.apiRate);
      const inlineRateValue = this.inlineRate ? parseFloat(this.inlineRate) : null;

      return inlineRateValue !== null && Math.abs(inlineRateValue - currentApiRate) > 0.001;
    },

    // Get a representative task for analysis calculations
    representativeTask() {
      if (this.relatedTasksData.length === 0) return null;
      // Use the most recent task as representative
      return [...this.relatedTasksData].sort((a, b) => new Date(b.execution?.[0]?.start || 0) - new Date(a.execution?.[0]?.start || 0))[0];
    },

    description() {
      return this.representativeTask?.description || '';
    },

    elapsed() {
      return this.representativeTask?.elapsed || 0;
    },

    currentUserDisplay() {
      // This will be populated when we refresh processing rate data after selecting a task
      const result = this.user_display ? this.user_display : null;
      console.log('currentUserDisplay computed:', result);
      return result;
    },

    currentTimestamp() {
      // This will be populated when we refresh processing rate data after selecting a task
      const result = this.timestamp ? this.timestamp : null;
      console.log('currentTimestamp computed:', result);
      return result;
    }
  },
  props: {
    foodid: {},
    team: {},
  }
  ,
  methods: {
    // Expose utility functions for template use
    calculateTotalWeight,
    formatWeight,
    calculateRate,
    formatRate,
    formatDuration,
    getTopWeightBins,
    getBinRepresentativeWeight,
    calculateEstimatedDuration,

    selectAllMembers() {
      this.selectedMembers = [];
    },
    getTaskMemberNames(task) {
      const namesSet = new Set();
      if (task && task.execution && task.execution.length > 0) {
        for (const execution of task.execution) {
          if (execution.team_members && execution.team_members.length > 0) {
            for (const member of execution.team_members) {
              if (member && member.name) {
                namesSet.add(member.name);
              }
            }
          }
        }
      }
      return Array.from(namesSet);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return moment(dateString).format('MMM D, YYYY');
    },


    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      return moment(timestamp).format('MMM D, YYYY [at] HH:mm');
    },

    cleanTaskTitle(title) {
      if (!title) return title;
      // Remove patterns like (P1), (P2), (PR), etc.
      return title.replace(/\s*\([A-Z]\d*\)\s*/g, '').trim();
    },
    async selectTask(task) {
      console.log('selecting task')
      // Prevent duplicate calls if already loading
      if (this.loadingRelated) {
        return;
      }
      this.title = task.title;
      this.inlineRate = task.apiRate;
      this.apiRate = task.apiRate;

      return this.selectFoodId(task.source ? task.source.foodid : null);
    },
    async selectFoodId(foodId) {
      console.log('select food id', foodId)
      if (this.loadingRelated) {
        console.log('already loading')
        return;
      }
      // Check if this is the same task already selected
      if (this.selectedFoodId === foodId) {
        console.log('already selected')
        return;
      }
      this.selectedFoodId = foodId;

      this.relatedTasksData = [];

      this.loadingRelated = true;
      try {
        console.log('fetching data');
        this.relatedTasksData = await this.findChoppingRelatedTasks();
        await this.fetchApiMedianRate();
        await this.fetchCurrentProcessingRateData();

        console.log('done fetching',this.relatedTasksData);

        // Set title from representative task
        if (this.representativeTask) {
          this.title = this.representativeTask.title;
          this.inlineRate = this.representativeTask.apiRate;
          this.apiRate = this.representativeTask.apiRate;
        }
      } finally {
        this.loadingRelated = false;
      }
    },
    goBackToTaskList() {
      this.$router.push({
        name: 'TaskList',
        query: {
          dates: this.dates,
          team: 'chopping'
        }
      });
    },
    async findChoppingRelatedTasks() {
      const selectedFoodId = this.selectedFoodId;

      if (!selectedFoodId) {
        return [];
      }

      try {
        let params = {
          sourceQuery: {
            path: ['foodid'],
            equals: Number(selectedFoodId)
          },
          minDuration: 1000 * 60 * 5, //5 min
          maxDuration: 1000 * 60 * 60 * 8, // 8 hrs
          includeProcessingRates: 'true'
        };
        console.log('fetching related tasks',params);
        const response = await api.post('/v2/task/related', params);

        const relatedTasks = response.data;

        return relatedTasks
          .map(task => ({
            ...task,
            relationReason: `Same Food ID: ${selectedFoodId}`
          }));

      } catch (error) {
        console.error('Failed to fetch related tasks:', error);
        return [];
      }
    },
    async fetchApiMedianRate() {
      const selectedFoodId = this.selectedFoodId;

      if (!selectedFoodId) {
        this.apiMedianRateData = null;
        return;
      }

      try {
        const response = await api.get(`/v2/task/median-rate/${selectedFoodId}`);
        this.apiMedianRateData = response.data;
      } catch (error) {
        console.error('Failed to fetch API median rate:', error);
        this.apiMedianRateData = null;
      }
    },

    async fetchCurrentProcessingRateData() {
      const selectedFoodId = this.selectedFoodId;

      if (!selectedFoodId) {
        this.currentEntryType = null;
        return;
      }

      try {
        const response = await api.get(`/v2/food/processing-rate/${selectedFoodId}`);
        const processingRate = response.data;

        if (processingRate) {
          console.log('Processing rate response:', processingRate);
          this.currentEntryType = processingRate.entry_type;

          // Store user display information
          if (processingRate.user_display) {
            this.$set(this, 'user_display', processingRate.user_display);
            console.log('Stored user display:', processingRate.user_display);
          }

          // Store timestamp information
          if (processingRate.timestamp) {
            this.$set(this, 'timestamp', processingRate.timestamp);
            console.log('Stored timestamp:', processingRate.timestamp);
          }
        } else {
          this.currentEntryType = null;
        }
      } catch (error) {
        console.error('Failed to fetch current processing rate data:', error);
        this.currentEntryType = null;
      }
    },
    isRateOutlier(task) {
      if (this.relatedTasksData.length < 4) return false;

      const bounds = this.currentRateBounds;
      if (bounds.lower === 0 && bounds.upper === 0) return false; // No valid stats

      const taskRate = calculateRate(task.description, task.elapsed);
      return taskRate < bounds.lower || taskRate > bounds.upper;
    },

    isDurationOutlier(task) {
      if (this.relatedTasksData.length < 4) return false;

      const bounds = this.currentDurationBounds;
      if (bounds.lower === 0 && bounds.upper === 0) return false; // No valid stats

      const taskDuration = task.elapsed / (1000 * 60 * 60); // Convert to hours
      return taskDuration < bounds.lower || taskDuration > bounds.upper;
    },

    // Inline rate editing methods
    async saveInlineProcessingRate() {
      if (!this.selectedFoodId) {
        console.error('No selected task or food ID available');
        return;
      }

      if (!this.inlineRateFormValid) {
        console.error('Inline form validation failed');
        return;
      }

      const foodId = this.selectedFoodId;
      const rate = parseFloat(this.inlineRate);
      const fixedTime = parseFloat(this.inlineFixedTime) || 0;

      if (isNaN(rate) || rate <= 0) {
        console.error('Invalid rate value');
        return;
      }

      this.inlineRateSaving = true;

      try {
        await api.post(`/v2/food/processing-rate/${foodId}`, {
          entry_type: 'manual-update',
          fixed_hours: fixedTime,
          piecewise_linear_data: [
            {
              weight_kg: 1,
              rate: rate // Save as kg/hr
            }
          ]
        });

        console.log(`Successfully saved processing rate for food ID ${foodId}: ${rate} kg/hr, fixed time: ${fixedTime} hours`);

        // Update the selected task's apiRate to reflect the new rate
        this.apiRate = rate.toFixed(2);

        // Refresh the processing rate data 
        try {
          const response = await api.get(`/v2/food/processing-rate/${foodId}`);
          const processingRate = response.data;

          if (processingRate) {
            console.log('Processing rate response after save:', processingRate);
            this.currentEntryType = processingRate.entry_type;

            // Store user display information
            if (processingRate.user_display) {
              this.$set(this, 'user_display', processingRate.user_display);
              console.log('Stored user display after save:', processingRate.user_display);
            }

            // Store timestamp information
            if (processingRate.timestamp) {
              this.$set(this, 'timestamp', processingRate.timestamp);
              console.log('Stored timestamp after save:', processingRate.timestamp);
            }

            if (processingRate.piecewise_linear_data && processingRate.piecewise_linear_data.length > 0) {
              const firstPoint = processingRate.piecewise_linear_data[0];
              if (firstPoint && firstPoint.rate) {
                this.apiRate = parseFloat(firstPoint.rate).toFixed(2);
              }
            }
          }
        } catch (error) {
          console.error('Failed to refresh processing rate data:', error);
        }

      } catch (error) {
        console.error(`Failed to save processing rate for food ID ${foodId}:`, error);
      } finally {
        this.inlineRateSaving = false;
      }
    },
  }
}
</script>

<style>
.row-hover tbody tr:hover {
  background-color: #f5f5f5 !important;
  cursor: pointer;
}

.outlier-indicator {
  margin-top: 2px;
  display: flex;
  align-items: center;
}

.estimate-section {
  margin-top: 8px;
  padding: 8px;
  background-color: #f8f9fa;
  border-left: 3px solid #1976d2;
  border-radius: 4px;
}

.estimate-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.estimate-values {
  margin-left: 4px;
  font-size: 14px;
}
</style>